import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpService } from './https.service';
import { URLUtils } from '../urlUtils';
import { QuestionMapperService } from './question-mapper.service';
import { FeedbackConfig, FeedbackQuestion, RawFeedbackQuestion, RawFeedbackResponse } from '../interfaces/feedback.model';

const DEFAULT_TITLE = 'Patient Support';
const DEFAULT_MESSAGE = 'Dear Patient 👋, We\'re sorry for the inconvenience. Please select a category to raise the complaint.';
const DEFAULT_THANKS = 'Thank you for sharing the details. Our team will get back to you soon. Would you like to raise another issue?';
const DEFAULT_CLOSING = 'Thank you for contacting Patient Support! Have a great day!';
const DEFAULT_NPS_TAGLINE = 'Compassion・Care・Trust';
const DEFAULT_NPS_QUESTION = 'How would you rate your overall experience with our services?';
const DEFAULT_NPS_THANKS = 'Thank you for your valuable feedback. Would you like to continue?';

@Injectable({
  providedIn: 'root'
})
export class FeedbackService {

  constructor(
    private httpService: HttpService,
    private questionMapper: QuestionMapperService
  ) {}

  getFeedbackConfig(firmName: string): Observable<FeedbackConfig> {
    return this.httpService.sendGetRequest(URLUtils.questions(firmName)).pipe(
      map((resp: unknown) => this.normalize(resp as RawFeedbackResponse)),
      catchError((err) => {
        console.error('[FeedbackService] GET /questions/{firmName} failed:', err);
        return throwError(() => err);
      })
    );
  }

  private normalize(raw: RawFeedbackResponse): FeedbackConfig {
    if (!raw || typeof raw !== 'object') {
      throw new Error('Empty or non-object feedback configuration response.');
    }

    if (raw.error === true) {
      throw new Error('Feedback configuration API reported error=true.');
    }

    if (!raw.uid || typeof raw.uid !== 'string') {
      throw new Error('Feedback configuration response is missing a valid uid.');
    }

    const rawCategories = Array.isArray(raw.data) ? raw.data : [];
    const categories = rawCategories
      .map((cat, idx) => this.questionMapper.mapCategory(cat, idx))
      .filter((c): c is FeedbackConfig['categories'][number] => c !== null);

    const isDynamic = categories.some(c => c.questions.length > 0);

    const uiSource = raw.settings?.ui ?? raw.ui;
    const complaintType = raw.settings?.type ?? raw.complaint_type;

    return {
      uid: raw.uid,
      complaintType: typeof complaintType === 'string' ? complaintType : '',
      ui: {
        title: this.text(uiSource?.title, DEFAULT_TITLE),
        message: this.text(uiSource?.message, DEFAULT_MESSAGE),
        thanks: this.text(uiSource?.thanks, DEFAULT_THANKS),
        closing: this.text(uiSource?.closing, DEFAULT_CLOSING),
        npsTagline: this.text(uiSource?.nps_tagline, DEFAULT_NPS_TAGLINE),
        npsQuestion: this.text(uiSource?.nps_question, DEFAULT_NPS_QUESTION),
        npsThanks: this.text(uiSource?.nps_thanks, DEFAULT_NPS_THANKS)
      },
      categories,
      isDynamic,
      details: this.normalizeDetails(raw.details),
      contactPreference: raw.settings?.features?.contact_preference === true
    };
  }

  private text(value: string | undefined, fallback: string): string {
    return typeof value === 'string' && value.trim() ? value : fallback;
  }

  /**
   * Firm-level questions asked at the end of the complaint. They are ordinary
   * questions - same shape, same renderer - so they go through the same mapper.
   */
  private normalizeDetails(raw: RawFeedbackQuestion[] | undefined): FeedbackQuestion[] {
    if (!Array.isArray(raw)) {
      return [];
    }

    return raw
      .map(q => this.questionMapper.mapQuestion(q))
      .filter((q): q is FeedbackQuestion => q !== null)
      .sort((a, b) => a.displayOrder - b.displayOrder);
  }
}
